import React, { useState } from 'react';
import {
  Grid, Box, Container, Typography, Stack, Button,
} from '@mui/material';
import MuiLink from '@mui/material/Link';
import {
  RiMapPinLine,
  RiTimer2Line,
  //  RiLuggageCartLine,
  RiPhoneLine,
  RiEarthLine,
} from 'react-icons/ri';
import { useSelector } from 'react-redux';
import GoogleMap from 'google-map-react';
import { ImgBox, Typography700 } from 'views/common/components';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';
import { mapDarkStyle } from 'views/rebranded-module/common/components/UI/InteractiveMap/utils/mapDarkStyle';
import NavigatorLink from 'views/rebranded-module/common/components/Logical/NavigatorLink';
import Flag from 'react-world-flags';
import StyledBox from './MapSection.styles';

const { envVars } = require('next/config').default().publicRuntimeConfig;

const MapSection = (props) => {
  const {
    dealer, location, phones, content,
  } = props;
  const { isCrawler } = useSelector((state) => state.globalData);
  const [showMap, setShowMap] = useState(!isCrawler);
  const handleShowMapClick = () => {
    setShowMap(true);
  };
  const mapOptions = (maps) => ({
    fullscreenControl: false,
    styles: mapDarkStyle,
    mapTypeControlOptions: {
      position: maps.ControlPosition.TOP_RIGHT,
    },
    zoomControlOptions: {
      position: maps.ControlPosition.TOP_RIGHT,
      style: maps.ZoomControlStyle.SMALL,
    },
  });

  const addressParts = [];
  if (location.address1) addressParts.push(location.address1);
  if (location.address2) addressParts.push(location.address2);
  const address = `${addressParts.join('<br>')}${
    addressParts.length ? '<br>' : ''
  }${location.full_address}`;

  return (
    <StyledBox>
      <Container className="LVCustomContaine">
        <Grid container spacing={4}>
          <Grid item xs={12} md={12} lg={6}>
            <Box className="MSMapWrapper">
              {showMap ? (
                <GoogleMap
                  bootstrapURLKeys={{ key: envVars.GOOGLE_MAPS_API_KEY }}
                  defaultCenter={{
                    lat: +location.latitude,
                    lng: +location.longitude,
                  }}
                  defaultZoom={12}
                  options={mapOptions}
                  yesIWantToUseGoogleMapApiInternals>
                  <Box lat={+location.latitude} lng={+location.longitude}>
                    <ImgBox
                      image={{
                        src: '/imgs/map-pin.svg',
                        alt: 'Eaglerider location pin',
                      }}
                      className="MSLocPin"
                    />
                  </Box>
                </GoogleMap>
              ) : (
                <Button
                  onClick={handleShowMapClick}
                  variant="outlined"
                  color="secondary">
                  {content.showMap}
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={12} lg={6}>
            <Stack gap={4}>
              <Stack gap={2}>
                {dealer?.image_url && (
                  <ImgBox
                    image={{
                      src: dealer.image_url,
                      alt: dealer.name,
                    }}
                    alt={dealer.name}
                    priority
                    imgObjectFit="contain"
                    imgWidth={144}
                    imgHeight={48}
                    objectPosition="left"
                  />
                )}
                <Typography variant="body2" className="upperCase">
                  {dealer
                    ? interpolateString(content.dealerMapSubtitle, {
                      location: location.display_name,
                    })
                    : content.mapSubtitle}
                </Typography>
                <Typography700 variant="h3">
                  {dealer
                    ? dealer.name
                    : interpolateString(content.mapTitle, {
                      location: location.display_name,
                    })}
                </Typography700>
              </Stack>

              <Stack gap={2}>
                <Box className="MSTextItem">
                  <RiMapPinLine className="MSTextItemIcon" />
                  <Typography700 variant="h7" component="p">
                    {content.address}
                  </Typography700>
                </Box>
                <NavigatorLink
                  target="_blank"
                  mapUrl={location.map_url}
                  className="MSFitContent">
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontWeight="500"
                    dangerouslySetInnerHTML={{ __html: address }}
                  />
                </NavigatorLink>
              </Stack>
              <Stack gap={2}>
                <Box className="MSTextItem">
                  <RiTimer2Line className="MSTextItemIcon" />
                  <Typography700 variant="h7" component="p">
                    {content.hours}
                  </Typography700>
                </Box>

                {location.compressed_operation_hours.map((item) => (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontWeight="500">
                    {interpolateString(content.operationTime, {
                      range: item.days_range,
                      from: item.from,
                      to: item.to,
                    })}
                  </Typography>
                ))}
                {location.working_hours_comment && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontWeight="500">
                    <Typography700
                      component="span"
                      variant="body1"
                      color="text.secondary">
                      {content.note}
                    </Typography700>
                    {location.working_hours_comment}
                  </Typography>
                )}
              </Stack>
              {/* commented untill business confirm if we already have this data provided by the business side. */}
              {/* <Stack gap={2}>
                <Box className="MSTextItem">
                  <RiLuggageCartLine className="MSTextItemIcon" />
                  <Typography700 variant="h7" component="p">
                    {content.parking_luggage}
                  </Typography700>
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight="500">
                  {parkingluggage}
                </Typography>
              </Stack> */}

              <Stack gap={2}>
                <Box className="MSTextItem">
                  <RiPhoneLine className="MSTextItemIcon" />
                  <Typography700 variant="h7" component="p">
                    {content.call_us}
                  </Typography700>
                </Box>
                <MuiLink className="MSContactTag" href={`tel:${phones[0]}`}>
                  {location.country.code && (
                    <Flag className="MSFlag" code={location.country.code} />
                  )}
                  <Typography700
                    variant="subtitle2"
                    component="p"
                    textTransform="uppercase">
                    {location.country.name}
                  </Typography700>
                  <Typography variant="body2">{phones[0]}</Typography>
                </MuiLink>
                {phones[1] && (
                  <MuiLink href={`tel:${phones[1]}`} className="MSContactTag">
                    <RiEarthLine className="MSInternationaIcon" />
                    <Typography700
                      variant="subtitle2"
                      fontWeight={700}
                      textTransform="uppercase">
                      {content.international}
                    </Typography700>
                    <Typography variant="body2">{phones[1]}</Typography>
                  </MuiLink>
                )}
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </StyledBox>
  );
};

export { MapSection };
